<!--
  StorefrontSearchBox.vue -- the storefront search control (topbar).

  Card: Storefront browse (https://trello.com/c/HUIrLhaU), pebble "Search".

  Jake, 2026-08-29: "Next to the Cart button I want a magnifying glass icon
  that expands to a search field. Search matches against product name and
  style/SKU. It should be a combo box where if I click an item in the list
  below the box it goes straight to that product's PDP. If I hit Enter or
  click the search icon again after typing something, I want a search result
  page".

  THE ICON HAS TWO JOBS, decided by whether the field holds a term:

    collapsed              -> expand + focus the field
    expanded, term typed   -> GO to the results page (Jake's "click the
                              search icon again after typing something")
    expanded, field empty  -> collapse again

  The third case is the one worth naming: with no term there is no results
  page to go to (a blank search is not a search), so the only sensible
  meaning left for the icon is "put this away". Navigating to an empty
  results page instead would strand the shopper on a page that can never
  show anything.

  SUGGESTIONS vs RESULTS. The dropdown is a shortcut, not the answer: it
  caps at SUGGESTION_LIMIT rows and each row navigates straight to that
  product's PDP. The full matching set lives on the results page. Both read
  the SAME `useStorefrontSearch` fetcher, so the dropdown can never claim a
  match the results page disagrees with.

  PDP LINK SHAPE. The product route is
  `/shop/<store>/c/<categorySlug>/p/<id>` and search is category-less, so
  these links use the EMPTY category segment (`/c//p/<id>`) -- the same
  empty-slug-is-home convention the Home grid uses, which the product
  route's `c/:categorySlug([^/]*)` regex matches deliberately. Inventing a
  `/search/p/<id>` shape would need a second product route for no gain.

  KEYBOARD NAVIGATION (WAI-ARIA APG combobox-with-listbox).

  Jake, 2026-08-29: "When I do a search, I want to use the up/down-arrows to
  walk through search results and Enter to go to one".

  DOM focus never leaves the input. The highlighted row is an ACTIVE OPTION
  -- a purely advisory pointer published as `aria-activedescendant` -- not a
  focused element. That is what lets the shopper keep typing mid-walk: real
  focus on a row would blur the field and tear the dropdown down.

  Four choices worth naming, because each has a plausible opposite:

  1. ArrowDown STOPS at the last row; ArrowUp from the first returns to the
     INPUT (no active option) rather than wrapping to the bottom. The
     no-active-option state is a real destination here -- Enter from it goes
     to the results page -- so it must stay reachable by keyboard. A wrapping
     list would make the shopper delete and retype to get back to it.

  2. ArrowDown OPENS a dismissed list when a term is still typed (APG), so
     Escape is recoverable without touching the text.

  3. Changing the term CLEARS the active option. An index outliving its
     result set points at whatever product now occupies that position -- so
     Enter would navigate somewhere the shopper never highlighted.

  4. Hover WRITES the active option rather than highlighting separately.
     Two independent highlights (one keyboard, one CSS `:hover`) would show
     two selected rows and let Enter commit the one the shopper is not
     looking at. There is exactly one selection model; the mouse and the
     arrows are two ways to move it.

  ENTER IS CONDITIONAL: with an active option it goes to that product's PDP;
  with none it keeps the shipped behaviour and submits to the results page.

  XSS: product names are distributor-authored and rendered here through
  `{{ }}` text interpolation only. There is no v-html sink in this
  component, and `storefront-search-box.spec.js` pins that a name
  containing markup renders as literal text.
-->
<template>
  <div class="sf-search" data-testid="sf-search">
    <!-- Field FIRST in DOM order, icon second: the icon keeps its position
         immediately beside the Cart control while the field grows to the
         LEFT, so opening search never shoves Cart sideways. -->
    <div v-if="expanded" class="sf-search__field-wrap">
      <input
        ref="input"
        v-model="term"
        type="search"
        class="sf-search__input"
        data-testid="sf-search-input"
        placeholder="Search products"
        aria-label="Search products"
        role="combobox"
        aria-autocomplete="list"
        :aria-expanded="showSuggestions ? 'true' : 'false'"
        aria-controls="sf-search-suggestions"
        :aria-activedescendant="activeOptionId"
        @input="onInput"
        @keydown.down.prevent="onArrowDown"
        @keydown.up.prevent="onArrowUp"
        @keydown.enter="onEnter"
        @keydown.esc="onEscape"
      />

      <ul
        v-if="showSuggestions"
        id="sf-search-suggestions"
        class="sf-search__suggestions"
        data-testid="sf-search-suggestions"
        role="listbox"
      >
        <li
          v-for="(product, index) in suggestions"
          :id="optionId(index)"
          :key="product.id"
          class="sf-search__suggestion"
          :class="{ 'sf-search__suggestion--active': index === activeIndex }"
          :data-testid="`sf-search-suggestion-${product.id}`"
          role="option"
          :aria-selected="index === activeIndex ? 'true' : 'false'"
          @mouseenter="activeIndex = index"
          @mousedown.prevent="goToProduct(product)"
        >
          <img
            v-if="product.thumbnail_url"
            :src="product.thumbnail_url"
            :alt="product.name"
            class="sf-search__suggestion-thumb"
            data-testid="sf-search-suggestion-thumb"
          />
          <span v-else class="sf-search__suggestion-thumb sf-search__suggestion-thumb--empty" />

          <span class="sf-search__suggestion-text">
            <span
              class="sf-search__suggestion-name"
              data-testid="sf-search-suggestion-name"
              >{{ product.name }}</span
            >
            <span
              v-if="product.style_number"
              class="sf-search__suggestion-sku"
              data-testid="sf-search-suggestion-sku"
              >{{ product.style_number }}</span
            >
          </span>
        </li>
      </ul>

      <div
        v-else-if="showNoMatches"
        class="sf-search__no-matches"
        data-testid="sf-search-no-matches"
      >
        No products match "{{ displayTerm }}".
      </div>
    </div>

    <button
      type="button"
      class="storefront-topbar__btn sf-search__toggle"
      data-testid="sf-search-toggle"
      :aria-label="expanded ? 'Search' : 'Open search'"
      title="Search"
      :aria-expanded="expanded ? 'true' : 'false'"
      @click="onToggle"
    >
      <span class="tf-icon" aria-hidden="true">search</span>
    </button>
  </div>
</template>

<script>
import { useStorefrontSearch } from "@/composables/useStorefrontSearch";

// How many rows the dropdown shows. A shortcut list, not the result set --
// the results page carries the rest.
const SUGGESTION_LIMIT = 8;

// Type-ahead debounce. Long enough that a normal typing burst issues ONE
// request rather than one per keystroke, short enough to feel immediate.
const DEBOUNCE_MS = 250;

// Cap on the term echoed into the "no products match" note. The results
// page has its own (larger) display cap; this one only has to keep the
// dropdown from stretching.
const NOTE_TERM_MAX = 40;

export default {
  name: "StorefrontSearchBox",

  props: {
    // The store whose products are searched. Passed in rather than
    // resolved here so the topbar owns slug resolution in ONE place.
    storeSlug: {
      type: String,
      default: "",
    },
  },

  setup() {
    const { products, loading, search } = useStorefrontSearch();
    return { products, loading, search };
  },

  data() {
    return {
      expanded: false,
      term: "",
      // True once a fetch for the CURRENT term has completed -- the
      // "no matches" note must not flash before the first response.
      fetched: false,
      debounceId: null,
      // Index of the ACTIVE OPTION in `suggestions`, or -1 for none.
      // -1 is not "row zero by another name": it is the state in which
      // Enter submits to the results page, so the two are behaviorally
      // distinct and both must be reachable.
      activeIndex: -1,
      // Set when Escape dismisses the list while a term is still typed.
      // ArrowDown clears it to re-open (APG), and any edit to the term
      // clears it too -- typing more is itself a request to see matches.
      dismissed: false,
    };
  },

  computed: {
    trimmedTerm() {
      return (this.term || "").trim();
    },

    suggestions() {
      return (this.products || []).slice(0, SUGGESTION_LIMIT);
    },

    showSuggestions() {
      return (
        !this.dismissed &&
        this.trimmedTerm.length > 0 &&
        this.suggestions.length > 0
      );
    },

    // The active option, or null when the shopper is still "in the input".
    activeProduct() {
      if (this.activeIndex < 0) return null;
      return this.suggestions[this.activeIndex] || null;
    },

    // `aria-activedescendant` -- undefined (not empty string) when nothing
    // is active, so the attribute is ABSENT rather than pointing nowhere.
    // A dangling id is worse than no id: assistive tech announces nothing
    // while the markup claims a selection exists.
    activeOptionId() {
      // Derived from `activeProduct`, not from the raw index: the index and
      // the list it addresses are moved by two different mechanisms (a key
      // handler and an async fetch), so the render must stay correct even
      // if they are momentarily out of step. Going through the resolved
      // product makes a dangling pointer unrepresentable.
      if (!this.showSuggestions || !this.activeProduct) return undefined;
      return this.optionId(this.activeIndex);
    },

    showNoMatches() {
      return (
        this.trimmedTerm.length > 0 &&
        this.fetched &&
        !this.loading &&
        this.suggestions.length === 0
      );
    },

    // Elided term for the no-matches note. Text interpolation escapes it;
    // the cap is a LAYOUT guard, not a security one.
    displayTerm() {
      const t = this.trimmedTerm;
      return t.length > NOTE_TERM_MAX ? `${t.slice(0, NOTE_TERM_MAX)}...` : t;
    },
  },

  beforeUnmount() {
    if (this.debounceId) clearTimeout(this.debounceId);
  },

  methods: {
    // Stable per-row DOM id. Distinct from the row's data-testid, which is
    // keyed by product id and belongs to the test/automation surface --
    // these two namespaces are kept apart deliberately.
    optionId(index) {
      return `sf-search-option-${index}`;
    },

    onArrowDown() {
      // APG: ArrowDown opens a dismissed list rather than being swallowed.
      if (this.dismissed) {
        this.dismissed = false;
        return;
      }
      if (!this.showSuggestions) return;
      // Stops at the end. See choice 1 in the header comment.
      if (this.activeIndex < this.suggestions.length - 1) {
        this.activeIndex += 1;
      }
    },

    onArrowUp() {
      if (!this.showSuggestions) return;
      // Walks back INTO the input (-1) and stays there -- no wrap.
      if (this.activeIndex >= 0) {
        this.activeIndex -= 1;
      }
    },

    onEnter() {
      const product = this.activeProduct;
      if (product) {
        this.goToProduct(product);
        return;
      }
      // No active option: the shipped behaviour, unchanged.
      this.submit();
    },

    onEscape() {
      // Escape peels one layer at a time: the active option first, then the
      // list, then the box. Collapsing outright would throw away a typed
      // term the shopper only meant to stop highlighting within.
      if (this.activeIndex >= 0) {
        this.activeIndex = -1;
        return;
      }
      if (this.showSuggestions) {
        this.dismissed = true;
        return;
      }
      this.collapse();
    },

    onToggle() {
      if (!this.expanded) {
        this.expand();
        return;
      }
      // Expanded: a typed term means "search it" (Jake's second click);
      // an empty field has no results page to go to, so it collapses.
      if (this.trimmedTerm) {
        this.submit();
      } else {
        this.collapse();
      }
    },

    expand() {
      this.expanded = true;
      this.$nextTick(() => {
        const el = this.$refs.input;
        if (el && typeof el.focus === "function") el.focus();
      });
    },

    collapse() {
      this.expanded = false;
      this.term = "";
      this.fetched = false;
      this.activeIndex = -1;
      this.dismissed = false;
      if (this.debounceId) {
        clearTimeout(this.debounceId);
        this.debounceId = null;
      }
    },

    onInput() {
      if (this.debounceId) clearTimeout(this.debounceId);

      // The term changed, so the previous result set is on its way out. An
      // index that survived would address a row in a list the shopper has
      // never seen -- Enter would then navigate to an arbitrary product.
      this.activeIndex = -1;
      // Typing is itself a request to see matches again.
      this.dismissed = false;

      if (!this.trimmedTerm) {
        // Clearing the field clears the list immediately -- no request, and
        // no stale suggestions left hanging under an empty box.
        this.fetched = false;
        this.search(this.storeSlug, "");
        return;
      }

      this.debounceId = setTimeout(() => {
        this.debounceId = null;
        this.runSearch();
      }, DEBOUNCE_MS);
    },

    async runSearch() {
      await this.search(this.storeSlug, this.trimmedTerm, {
        perPage: SUGGESTION_LIMIT,
      });
      this.fetched = true;
    },

    goToProduct(product) {
      if (!product || product.id === undefined || product.id === null) return;
      // Empty category segment -- see the PDP LINK SHAPE note above.
      this.$router.push(`/shop/${this.storeSlug}/c//p/${product.id}`);
      this.collapse();
    },

    submit() {
      const q = this.trimmedTerm;
      // A blank term is not a search: no navigation, and the field stays
      // open so the shopper can simply keep typing.
      if (!q) return;

      this.$router.push({
        path: `/shop/${this.storeSlug}/search`,
        query: { q },
      });
      this.collapse();
    },
  },
};
</script>

<style scoped>
.sf-search {
  display: inline-flex;
  align-items: center;
  position: relative;
}

/* The field + dropdown share a positioning context so the list can hang
   directly under the input regardless of the topbar's own layout. */
.sf-search__field-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.sf-search__input {
  width: 200px;
  max-width: 40vw;
  padding: 6px 10px;
  border: 1px solid var(--tf-border, #e0e0e0);
  border-radius: var(--tf-radius-sm, 6px);
  background: var(--tf-surface, #ffffff);
  color: var(--tf-fg-1, #212121);
  font-size: 14px;
  outline-offset: 2px;
}

.sf-search__input:focus {
  border-color: var(--tf-primary, #119ae9);
}

.sf-search__suggestions {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 20;
  width: 320px;
  max-width: 80vw;
  margin: 0;
  padding: 4px 0;
  list-style: none;
  background: var(--tf-surface, #ffffff);
  border: 1px solid var(--tf-border, #e0e0e0);
  border-radius: var(--tf-radius-sm, 6px);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  max-height: 60vh;
  overflow-y: auto;
}

.sf-search__suggestion {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  cursor: pointer;
}

/* Hover and keyboard share ONE highlight. `@mouseenter` writes activeIndex,
   so the visual state below is driven by that single source rather than by
   a separate `:hover` rule -- two highlight rules would let the shopper see
   two selected rows at once and press Enter on the wrong one. */
.sf-search__suggestion--active {
  background: var(--tf-surface-light, #f5f8fa);
}

.sf-search__suggestion-thumb {
  width: 36px;
  height: 36px;
  object-fit: cover;
  border-radius: var(--tf-radius-sm, 6px);
  flex: 0 0 auto;
  background: var(--tf-surface-light, #f5f8fa);
}

.sf-search__suggestion-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.sf-search__suggestion-name {
  font-size: 14px;
  color: var(--tf-fg-1, #212121);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sf-search__suggestion-sku {
  font-size: 12px;
  color: var(--tf-fg-3, #616161);
}

.sf-search__no-matches {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 20;
  width: 320px;
  max-width: 80vw;
  padding: 10px 12px;
  background: var(--tf-surface, #ffffff);
  border: 1px solid var(--tf-border, #e0e0e0);
  border-radius: var(--tf-radius-sm, 6px);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  font-size: 13px;
  color: var(--tf-fg-3, #616161);
  /* A long term is elided in `displayTerm`; this is the second guard so a
     single unbroken string still cannot widen the panel. */
  overflow-wrap: anywhere;
}
</style>
